<template>
  <div class="flex flex-col gap-y-4">
    <div v-if="!status" class="text-neutral">
      —
    </div>
    <template v-else>
      <!-- Status indicator -->
      <div class="flex items-center gap-x-3">
        <span
          class="inline-block w-3 h-3 rounded-full"
          :class="statusDotClass"
        />
        <span class="text-body-regular-bold">
          {{ $t(`pages.settings.integrations.status.${status.status}`) }}
        </span>
      </div>

      <!-- Connected details -->
      <template v-if="status.status === 'CONNECTED'">
        <div v-if="status.updatedAt" class="text-sm text-neutral">
          {{ $t('pages.settings.integrations.connectedAt') }}:
          {{ formatDate(status.updatedAt) }}
        </div>
        <div v-if="status.connectedBy" class="text-sm text-neutral">
          {{ $t('pages.settings.integrations.connectedBy') }}:
          {{ status.connectedBy }}
        </div>
        <div v-if="status.scope" class="text-sm text-neutral">
          {{ $t('pages.settings.integrations.scope') }}:
          {{ status.scope }}
        </div>
        <div v-if="status.instanceUrl" class="text-sm text-neutral">
          {{ $t('pages.settings.integrations.instanceUrl') }}:
          {{ status.instanceUrl }}
        </div>
      </template>

      <!-- Reauth required details -->
      <template v-if="status.status === 'REAUTH_REQUIRED'">
        <div v-if="status.lastError" class="text-sm text-red">
          {{ $t('pages.settings.integrations.lastError') }}:
          {{ status.lastError }}
        </div>
      </template>

      <!-- Action button -->
      <div class="mt-2">
        <custom-button
          v-if="status.status !== 'CONNECTED'"
          variant="primary"
          :busy="loading"
          @click="emit('connect')"
        >
          {{ status.status === 'REAUTH_REQUIRED'
            ? $t('pages.settings.integrations.reconnect')
            : $t('pages.settings.integrations.connect')
          }}
        </custom-button>
        <custom-button
          v-else
          variant="outlined"
          :busy="loading"
          @click="emit('connect')"
        >
          {{ $t('pages.settings.integrations.reconnect') }}
        </custom-button>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import CustomButton from '@components/utils/CustomButton.vue';
import { format } from 'date-fns';

interface IntegrationStatusResponse {
  status: 'NOT_CONNECTED' | 'CONNECTED' | 'REAUTH_REQUIRED' | 'DISCONNECTED';
  updatedAt?: string;
  connectedBy?: string;
  lastError?: string;
  scope?: string;
  instanceUrl?: string;
}

interface Props {
  status: IntegrationStatusResponse | null;
  loading?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{ connect: [] }>();

const statusDotClass = computed(() => {
  switch (props.status?.status) {
    case 'CONNECTED': return 'bg-success-green';
    case 'REAUTH_REQUIRED': return 'bg-yellow';
    case 'DISCONNECTED': return 'bg-red';
    default: return 'bg-element-neutral';
  }
});

const formatDate = (dateStr: string) => {
  try {
    return format(new Date(dateStr), 'dd.MM.yyyy HH:mm');
  } catch {
    return dateStr;
  }
};
</script>

